import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  TBctxItemWithoutSize,
  TBctxItemWithoutSizeAndBctxKey,
  TBulkCountAppDBStore,
  TBulkCountPendingTransactionWithBctxList,
  TBulkGradingPageSizeItem,
  TBulkWashingEventType,
  TEventStationDetails,
  TREQUEST_WipByBctxRequest,
  TSelectedStationIdAndShiftId,
  TSizeToSavedCountMap,
  TSizeToUserDBActualMapItem,
  TSizeToUserDBWipMapItem,
  TVendorDTO,
} from '@monorepo/types';
import { getEventsBatchCount, getOnlineStatus } from '../metadata/metadata.query';
import { getBctxItemsOrderWithoutSize, getConfigurations, selectedSubject } from '../app-master-db/app-master.query';
import { BulkCountAppComputationHelper } from '@monorepo/utils';
import { selectedShift, selectedShiftId } from '../shifts/shifts.query';

export const selectBulkCountAppDB = createFeatureSelector<TBulkCountAppDBStore>('BULK_COUNT_APP');

export const getAllVendors = createSelector(selectBulkCountAppDB, (bulkCountAppDB) => {
  return bulkCountAppDB.vendors.vendors;
});

export const getSelectedStationAndShiftDTO = createSelector(
  selectedShift,
  selectedSubject,
  (selectedShift, selectedSubject) => {
    return {
      subject: selectedSubject as unknown,
      shift: selectedShift as unknown,
    } as TEventStationDetails;
  },
);

export const getUserDBLockDetailsAndNextCacheResetTime = createSelector(
  selectBulkCountAppDB,
  (selectBulkCountAppDB) => {
    return {
      lockDetails: selectBulkCountAppDB.userDBLockDetails,
      nextCacheResetTimestamp: selectBulkCountAppDB.userDB.dailyCache.nextCacheResetTimestamp,
    };
  },
);

export const getBgDBLockDetailsAndNextCacheResetTime = createSelector(selectBulkCountAppDB, (selectBulkCountAppDB) => {
  return {
    lockDetails: selectBulkCountAppDB.bgDBLockDetails,
    nextCacheResetTimestamp: selectBulkCountAppDB.bgDB.dailyCache.nextCacheResetTimestamp,
  };
});

export const selectUserDB = createSelector(selectBulkCountAppDB, (store) => store.userDB);

export const getUserDBAndBgDBLockDetails = createSelector(selectBulkCountAppDB, (db) => {
  return {
    bgDBLockDetails: db.bgDBLockDetails,
    userDBLockDetails: db.userDBLockDetails,
  };
});

export const getSelectedStationIdAndShiftId = createSelector(
  selectedShiftId,
  selectedSubject,
  (selectedShiftId, selectedSubject) => {
    const stationIdAndShiftId: TSelectedStationIdAndShiftId = {
      stationId: selectedSubject ? selectedSubject.subject : '',
      shiftId: selectedShiftId ? selectedShiftId : '',
    };
    return stationIdAndShiftId;
  },
);

export const getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift = createSelector(
  selectBulkCountAppDB,
  getOnlineStatus,
  getEventsBatchCount,
  getSelectedStationIdAndShiftId,
  getConfigurations,
  (store, isOnline, batchCount, selectedStationIdAndShiftId, config) => {
    return {
      userDB: store.userDB,
      bgDB: store.bgDB,
      isOnline,
      batchCount,
      selectedStationIdAndShiftId,
      retryCount: config.retryCount,
      isUserDBLocked: store.userDBLockDetails.isLocked,
    };
  },
);

export const getBctxWithSizeList = createSelector(
  selectBulkCountAppDB,
  getSelectedStationIdAndShiftId,
  (currentDB, selectedStationAndShiftId) => {
    const { stationId } = selectedStationAndShiftId;
    const bctxWithoutSizeList: TREQUEST_WipByBctxRequest['bctxList'] = [];
    Object.entries(currentDB.userDB.dailyCache.lineMap[stationId].bctxAndVendorWipMap).forEach(([key, item]) => {
      bctxWithoutSizeList.push({
        bctxKeyValues: item.bctxItemListWithoutSize,
        sizes: Object.keys(item.sizeMap),
      });
    });
    return bctxWithoutSizeList;
  },
);

export const getBctxItemWithoutSizeAndBctxKey = (
  vendorId: TVendorDTO['vendorId'] | undefined,
  eventType: TBulkWashingEventType,
) =>
  createSelector(selectBulkCountAppDB, getSelectedStationIdAndShiftId, (currentDB, selectedStationAndShiftId) => {
    const bctxList: TBctxItemWithoutSizeAndBctxKey[] = [];
    Object.entries(
      currentDB.userDB.dailyCache.lineMap[selectedStationAndShiftId.stationId].bctxAndVendorWipMap,
    ).forEach(([key, item]) => {
      let isValid = false;
      if (vendorId) {
        const vendorIdLength = vendorId.toString().length;
        isValid =
          key.length <= vendorIdLength ? false : key.substring(key.length - vendorIdLength) === vendorId.toString();
      }
      if (eventType === 'SEND' || isValid) {
        bctxList.push({
          bctxKey: key,
          list: item.bctxItemListWithoutSize,
        });
      }
    });
    return bctxList;
  });

export const getSizeList = (
  eventType: TBulkWashingEventType,
  bctxList: TBctxItemWithoutSize[],
  vendorId: TVendorDTO['vendorId'] | undefined,
) =>
  createSelector(
    selectBulkCountAppDB,
    getSelectedStationIdAndShiftId,
    getBctxItemsOrderWithoutSize,
    (currentDB, selectedStationAndShiftId, orderKeys) => {
      const bctxKeyAndVendorId = BulkCountAppComputationHelper.getUniqueBctxKeyAndVendorIdWithOrder(
        orderKeys,
        bctxList,
        eventType === 'RECEIVE' ? vendorId : undefined,
      );
      const { stationId, shiftId } = selectedStationAndShiftId;
      const mergedUserDBLineMap = BulkCountAppComputationHelper.mergeUserLineMapWithBgLineMap(
        currentDB.userDB.dailyCache.lineMap,
        currentDB.bgDB.dailyCache.lineMap,
      );
      const bctxAndVendorWipMapItemLineLevel = mergedUserDBLineMap[stationId].bctxAndVendorWipMap[bctxKeyAndVendorId];
      const bctxAndVendorWipMapShiftLevel = mergedUserDBLineMap[stationId].shiftMap[shiftId][bctxKeyAndVendorId];

      const sizeList: TBulkGradingPageSizeItem[] = [];
      const sizeMap: Record<string, TBulkGradingPageSizeItem> = {};
      if (eventType === 'SEND') {
        Object.entries(bctxAndVendorWipMapShiftLevel.sizeMap).forEach(([key, item]) => {
          const sizeItem = item as TSizeToUserDBActualMapItem;
          sizeMap[key] = {
            name: sizeItem.name,
            wipQty: 0,
            activeQty: 0,
            sentQty: 0,
            receiveQty: sizeItem.totalCompletedCount,
          };
        });
        Object.entries(bctxAndVendorWipMapItemLineLevel.sizeMap).forEach(([key, item]) => {
          const sizeItem = item as TSizeToUserDBWipMapItem;
          if (sizeMap[key]) {
            sizeMap[key] = {
              ...sizeMap[key],
              wipQty: sizeItem.wipQty,
            };
          }
        });
      } else {
        Object.entries(bctxAndVendorWipMapShiftLevel.sizeMap).forEach(([key, item]) => {
          const sizeItem = item;
          sizeMap[key] = {
            name: sizeItem.name,
            wipQty: sizeItem.sentQty - sizeItem.totalCompletedCount,
            activeQty: 0,
            sentQty: sizeItem.sentQty,
            receiveQty: sizeItem.totalCompletedCount,
          };
        });
      }
      const pendingTransactions = currentDB.userDB.persistanceCache.pendingTransactions;
      const foundPendingTransaction = pendingTransactions.find(
        (item) => item.shiftId === shiftId && item.lineId === stationId && item.bctxKey === bctxKeyAndVendorId,
      );

      if (foundPendingTransaction) {
        Object.entries(foundPendingTransaction.sizeMap).forEach(([_x, sizeMapItem]) => {
          const sizeItem = sizeMapItem;
          if (sizeMap[sizeItem.name]) {
            sizeMap[sizeItem.name] = {
              ...sizeMap[sizeItem.name],
              activeQty: sizeItem.savedCount,
            };
          }
        });
      }
      Object.entries(sizeMap).forEach(([_, item]) => {
        sizeList.push(item);
      });
      return sizeList;
    },
  );

export const getTotalQty = createSelector(selectBulkCountAppDB, (db) => {
  return db.userDB.dailyCache.totalCount + db.bgDB.dailyCache.totalCount;
});

export const getTransactions = createSelector(selectBulkCountAppDB, (db) => {
  return db.transactions.res;
});

export const getPendingTransactionsWithBctxList = createSelector(
  selectBulkCountAppDB,
  getSelectedStationIdAndShiftId,
  getAllVendors,
  (currentDB, selectedStationAndShiftId, vendors) => {
    const { stationId, shiftId } = selectedStationAndShiftId;
    const pendingTransactions = currentDB.userDB.persistanceCache.pendingTransactions.filter(
      (item) => item.lineId === stationId && item.shiftId === shiftId,
    );
    const bctxAndVendorWipMap = currentDB.userDB.dailyCache.lineMap[stationId].bctxAndVendorWipMap;
    const pendingTransactionsWithBctxList: TBulkCountPendingTransactionWithBctxList[] = pendingTransactions.map(
      (transaction) => {
        return {
          bcxtList: bctxAndVendorWipMap[transaction.bctxKey].bctxItemListWithoutSize,
          transaction,
          vendor: vendors.find((vendor) => vendor.vendorId === transaction.vendorId),
        };
      },
    );
    return pendingTransactionsWithBctxList;
  },
);
